import { uiSurface } from "shared/ui/uiSurface"
import {
  Add,
  Cancel,
  CheckBox,
  Close,
  DirectionsCar,
  Error as ErrorIcon,
  GridView,
  Home,
  KeyboardArrowDown,
  LocalShipping,
  Place,
  Replay,
  Search,
  Settings,
} from "@mui/icons-material"
import {
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { Dayjs } from "dayjs"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { useSidebarStyle } from "../../hooks/useSidebarStyle"
import { useUiDensity } from "../../hooks/useUiDensity"
import { StatusGroup } from "./ordersPageConfig"

const iconMap: Record<string, FC<{ sx?: any }>> = {
  Build: Settings,
  Inventory: LocalShipping,
  LocalShipping: DirectionsCar,
  CheckCircle: CheckBox,
  Store: Replay,
  Warning: ErrorIcon,
  Cancel,
}

type OrdersFilterPanelProps = {
  date: Dayjs
  onDateChange: (d: Dayjs | null) => void
  groups: StatusGroup[]
  groupCounts: Record<string, number>
  selectedGroup: string | null
  onSelectGroup: (key: string) => void
  search: string
  onSearchChange: (value: string) => void
  onClose?: () => void
  onCreateOrder?: () => void
  viewMode?: "grid" | "map"
  onViewModeChange?: (mode: "grid" | "map") => void
}

export const OrdersFilterPanel: FC<OrdersFilterPanelProps> = ({
  date,
  onDateChange,
  groups,
  groupCounts,
  selectedGroup,
  onSelectGroup,
  search,
  onSearchChange,
  onClose,
  onCreateOrder,
  viewMode = "grid",
  onViewModeChange,
}) => {
  const { t } = useTranslation()
  const { tokens } = useUiDensity()
  const { colors: c } = useSidebarStyle()

  return (
    <Box
      {...uiSurface("UI-DLV2-021")}
      data-testid="orders-filter-panel"
      sx={{
        width: tokens.sidebarWidth,
        minWidth: tokens.sidebarWidth,
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: c.bg,
        color: c.text,
      }}
    >
      {/* Close button — shown when inside a drawer */}
      {onClose && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1, pt: 1 }}>
          <IconButton size="small" onClick={onClose} sx={{ color: c.iconColor }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* New Order button */}
      <Box sx={{ px: tokens.sectionPx, pt: onClose ? 0 : tokens.sectionPx, pb: tokens.sectionPy }}>
        <Button
          data-testid="orders-create-btn"
          variant="contained"
          fullWidth
          startIcon={<Add />}
          onClick={onCreateOrder}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            py: tokens.buttonPy,
            fontSize: tokens.fontSize,
            bgcolor: c.buttonBg,
            color: c.buttonText,
            "&:hover": { bgcolor: c.buttonBgHover },
          }}
        >
          {t("orders.create")}
        </Button>
      </Box>

      {/* Date picker */}
      <Box
        data-testid="orders-date-picker"
        sx={{ px: tokens.sectionPx, pb: tokens.sectionPy, color: c.datePickerTextColor }}
      >
        <DatePicker
          value={date}
          onChange={onDateChange}
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              sx: {
                backgroundColor: c.datePickerBg,
                borderRadius: 1,
                "& .MuiInputBase-input": {
                  color: c.datePickerTextColor,
                },
                "& .MuiIconButton-root": {
                  color: c.datePickerIconColor,
                },
                "& fieldset": {
                  borderColor: c.datePickerBorderColor,
                },
              },
            },
          }}
        />
      </Box>

      {/* View toggle */}
      <Box sx={{ display: "flex", justifyContent: "center", pb: tokens.sectionPy }}>
        <ToggleButtonGroup
          {...uiSurface("UI-DLV2-023")}
          data-testid="orders-view-toggle"
          value={viewMode}
          exclusive
          size="small"
          onChange={(_e, val) => { if (val) onViewModeChange?.(val) }}
          sx={{
            "& .MuiToggleButton-root": {
              color: c.toggleColor,
              borderColor: c.toggleBorder,
              "&.Mui-selected": {
                bgcolor: c.toggleSelectedBg,
                color: c.toggleSelectedColor,
              },
            },
          }}
        >
          <ToggleButton data-testid="orders-view-grid-btn" value="grid">
            <GridView fontSize="small" />
          </ToggleButton>
          <ToggleButton data-testid="orders-view-map-btn" value="map">
            <Place fontSize="small" />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Order status header */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: tokens.sectionPx,
          py: tokens.sectionPy,
          borderTop: 1,
          borderColor: c.border,
        }}
      >
        <Home sx={{ fontSize: 18, color: c.textDimmed }} />
        <Typography variant="body2" sx={{ flex: 1, fontWeight: 500, color: c.text }}>
          {t("ordersPage.orderStatus")}
        </Typography>
        <KeyboardArrowDown sx={{ fontSize: 18, color: c.iconMuted }} />
      </Stack>

      {/* Status groups */}
      <List data-testid="orders-status-group-list" dense disablePadding>
        {groups.map((group) => {
          const Icon = iconMap[group.icon]
          const count = groupCounts[group.key] ?? 0
          const isSelected = selectedGroup === group.key

          return (
            <ListItemButton
              data-testid={`orders-group-${group.key}`}
              key={group.key}
              selected={isSelected}
              onClick={() => onSelectGroup(group.key)}
              sx={{
                py: tokens.listItemPy,
                px: tokens.sectionPx,
                "&.Mui-selected": {
                  bgcolor: c.bgSelected,
                  "&:hover": { bgcolor: c.bgSelectedHover },
                },
                "&:hover": {
                  bgcolor: c.bgHover,
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {Icon && (
                  <Icon
                    sx={{
                      fontSize: 18,
                      color: isSelected ? c.chipSelectedText : c.iconMuted,
                    }}
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primary={t(group.labelKey)}
                slotProps={{
                  primary: {
                    variant: "body2",
                    sx: {
                      color: isSelected ? c.chipSelectedText : c.textMuted,
                      fontWeight: isSelected ? 600 : 400,
                    },
                  },
                }}
              />
              <Chip
                data-testid={`orders-group-${group.key}-count`}
                label={count}
                size="small"
                sx={{
                  height: tokens.chipHeight,
                  minWidth: 28,
                  bgcolor: isSelected ? c.chipSelectedBg : c.chipBg,
                  color: isSelected ? c.chipSelectedText : c.chipText,
                  fontWeight: 600,
                  fontSize: tokens.chipFontSize,
                }}
              />
            </ListItemButton>
          )
        })}
      </List>

      {/* Customer search */}
      <Box sx={{ borderTop: 1, borderColor: c.border, mx: tokens.sectionPx, mt: tokens.sectionPy }} />
      <Box sx={{ px: tokens.sectionPx, pt: tokens.sectionPy + 0.5, pb: tokens.sectionPx }}>
        <Stack direction="row" spacing={0.5}>
          <TextField
            data-testid="orders-search-input"
            size="small"
            fullWidth
            placeholder={t("ordersPage.customerSearchPlaceholder")}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                bgcolor: c.searchBg,
                borderRadius: 1,
                fontSize: 13,
              },
              "& fieldset": {
                borderColor: c.searchBorder,
              },
            }}
          />
          <IconButton
            data-testid="orders-search-btn"
            size="small"
            sx={{
              bgcolor: c.searchBtnBg,
              color: c.searchBtnColor,
              borderRadius: 1,
              px: 1.5,
              "&:hover": { bgcolor: c.searchBtnBgHover },
            }}
          >
            <Search fontSize="small" />
          </IconButton>
        </Stack>
      </Box>
    </Box>
  )
}
